import React, { useEffect, useRef, useState } from 'react';
import { Nav, Input } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import apiConfigurations from 'api/configurations';
import apiProjects from 'api/projects';
import animate from 'services/configurator/canvas/animate';
import getMouse from 'services/configurator/canvas/getMouse';
import IGlassAreaMetaData from 'components/interfaces/IGlassAreaMetaData';
import IRouteParams from 'interfaces/IRouteParams';
import IWallMetaData from 'components/interfaces/IWallMetaData';
import openSaveData from 'services/configurator/openSaveData';
import setEnviroment from 'services/configurator/canvas/setEnvironment';
import setupConfigurator from 'services/configurator/setupConfigurator';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';
import { setConfiguration } from 'store/configuration/actions';
import { setSettings } from 'store/configurationViewer/actions';
import { setWallsMetaData } from 'store/wallsMetaData/actions';

import { allowedToWriteOwnOrganizationConfiguration } from './services/permissions/allowedToWriteOwnOrganizationConfiguration';
import initializeSockets from './initializeSockets';
import LeftBar from './leftBar/LeftBar';
import PropertiesBar from './propertiesBar/PropertiesBar';
import Properties from '../../assets/properties.svg';
import RightBar from './rightBar/RightBar';
import InfoBar from './infoBar/InfoBar';
import { THREE_D, TWO_D } from './types/dimensions';

import 'style/configurator/Configurator.css';

const Configurator = (): JSX.Element | null => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dispatch = useDispatch();
  const [propertiesBarOpen, setPropertiesBarOpen] = useState(false);
  const [infoData, setInfoData] = useState<any>({});
  const [infoBarOpen, setInfoBarOpen] = useState(false);
  const [dimensionStatus, setDimensionStatus] = useState(TWO_D);
  const intersected = useRef(null);
  const { configurationId, projectId } = useParams<IRouteParams>();
  const [configName, setConfigName] = useState('');
  const [configData, setConfigData] = useState('');
  const [wallsData, setWallsData] = useState([]);
  const currentUser = useSelector((state: any) => state.users.current);
  const configuration = useSelector((state: any) => state.configuration);

  const settings = useSelector(
    (state: any) => state.configurationViewer.settings
  );

  const openInfoBar = (data: any) => {
    setInfoData(data);
    setInfoBarOpen(true);
  };

  const { t } = useTranslation([translations.common]);

  const updateSettings = () => {
    dispatch(
      setSettings(
        setupConfigurator({
          canvasRef,
          status: dimensionStatus,
        })
      )
    );
  };

  useEffect(() => {
    const handleConfigurationClose = () => {
      dispatch(setConfiguration(null));
    };

    if (settings !== null) {
      // Opening a second configuration without page refresh has problems
      // with reinitializing state. This causes for example the bug IN-367.
      // I used quite much time to fix this in a nice way but did not find
      // a solution. If you want to remove this, at least test that clicking
      // wall lines in a second configuration without page refresh succeeds
      // on every click.
      window.location.reload();
      return handleConfigurationClose;
    }

    const sortGlassAreas = (glassAreas: IGlassAreaMetaData[]) => {
      const sortedGlassAreas = [...glassAreas];

      sortedGlassAreas.sort(
        (glassArea1: IGlassAreaMetaData, glassArea2: IGlassAreaMetaData) => {
          if (glassArea1.index === glassArea2.index) {
            return 0;
          }

          return glassArea1.index < glassArea2.index ? 0 : 1;
        }
      );

      return sortedGlassAreas;
    };

    updateSettings();
    initializeSockets();
    if (configurationId === undefined) {
      const getProject = async () => {
        try {
          const project = await apiProjects.get(projectId);
          dispatch(
            setConfiguration({ createdUser: null, name: null, project })
          );
        } catch (error) {
          dispatch(addMessage(t(`${translations}:Get project failed`)));
        }
      };

      getProject();
    } else {
      const getConfiguration = async () => {
        try {
          const result = await apiConfigurations.get(configurationId);

          setWallsData(result.walls);
          setConfigName(result.name);
          setConfigData(result.data);

          dispatch(
            setConfiguration({
              createdUser: result.createdUser,
              name: result.name,
              project: result.project,
            })
          );

          dispatch(
            setWallsMetaData(
              result.walls.map((wall: IWallMetaData) => ({
                ...wall,
                glassAreas: sortGlassAreas(wall.glassAreas),
              }))
            )
          );
        } catch (error) {
          dispatch(addMessage(t(`${translations}:Get configuration failed`)));
        }
      };
      getConfiguration().catch((e) => console.log(e));
    }

    return handleConfigurationClose;
  }, []);

  useEffect(() => {
    if (configData !== '') {
      openSaveData(configData, wallsData, () => {
        setDimensionStatus(THREE_D);
      });
    }
  }, [configData]);

  useEffect(() => {
    if (settings === undefined || settings === null) {
      return;
    }

    setEnviroment({
      camera: settings.camera,
      controls: settings.controls,
      scene: settings.scene,
      status: dimensionStatus,
    });
  }, [dimensionStatus]);

  useEffect(() => {
    if (settings === null || settings === undefined) {
      return;
    }

    animate({
      ...settings,
      intersected,
      mouse: getMouse(),
    });

    const mouseEventHandler = (e: MouseEvent) => {
      if (canvasRef.current === null) {
        return;
      }

      const x = e.clientX - canvasRef.current.offsetLeft;
      const y = e.clientY - canvasRef.current.offsetTop;

      const mouse = getMouse();
      mouse.x = (x / canvasRef.current.width) * 2 - 1;
      mouse.y = -(y / canvasRef.current.height) * 2 + 1;

      animate({
        ...settings,
        intersected,
        mouse,
      });
    };

    document.addEventListener('click', (e) => {
      setTimeout(() => {
        mouseEventHandler(e);
      }, 100);
    });

    document.addEventListener('mousemove', (e) => {
      e.preventDefault();
      mouseEventHandler(e);
    });

    document.addEventListener('keydown', (e) => {
      const arrowKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (arrowKeys.includes(e.key)) {
        e.preventDefault();
        animate({
          ...settings,
          intersected,
          mouse: getMouse(),
        });
      }
    });
  }, [settings]);

  const showWriteActions =
    currentUser.allowedToWriteConfigurations ||
    allowedToWriteOwnOrganizationConfiguration(
      currentUser,
      configuration?.project
    );

  return (
    <>
      <Input id="buildingwallcolor" style={{ display: 'none' }} />
      <Input id="profilecolor" style={{ display: 'none' }} />
      <Input id="walldirection" style={{ display: 'none' }} />
      <PropertiesBar
        dimensionStatus={dimensionStatus}
        propertiesBarOpen={propertiesBarOpen}
        hasWritePermissions={showWriteActions}
      />
      {infoBarOpen ? (
        <InfoBar infoData={infoData} setInfoBarOpen={setInfoBarOpen} />
      ) : null}
      {showWriteActions ? (
        <Nav
          className="navmenu-properties-button"
          onClick={() => {
            setPropertiesBarOpen(!propertiesBarOpen);
            setInfoBarOpen(false);
          }}
        >
          <img className="properties-icon" alt="properties" src={Properties} />
          <h3 className="navmenu-properties-text">
            {t(`${translations.common}:Properties`).toLocaleUpperCase()}
          </h3>
        </Nav>
      ) : null}
      <LeftBar
        configName={configName}
        dimensionStatus={dimensionStatus}
        setDimensionStatus={setDimensionStatus}
        settings={settings}
        openInfoBar={openInfoBar}
      />
      <RightBar dimensionStatus={dimensionStatus} />
      <canvas id="configurator-canvas" ref={canvasRef} />
    </>
  );
};

export default Configurator;
